import { SUPPORT_FRENCH } from "../config/supportFrench";
import { normalizeLangParam, resolveInterfaceLang } from "../lib/interfaceLang";

export const CONTENT_LANG_KEY = "content.lang";
export const INTERFACE_LANG_KEY = "ui.lang";
export const ADMIN_CONTENT_LANG_KEY = "admin.content.lang";

// Language configuration with country codes for flags
// English and French first, then ordered by total speakers (native + second language) population
export const LANGUAGES = [
    { code: 'english', countryCode: 'US', name: 'English' },      // ~1.5 billion
    { code: 'french', countryCode: 'FR', name: 'Français' },
    { code: 'chinese', countryCode: 'CN', name: '中文' },         // ~1.3 billion
    { code: 'hindi', countryCode: 'IN', name: 'हिन्दी' },          // ~600 million
    { code: 'spanish', countryCode: 'ES', name: 'Español' },      // ~500 million
    { code: 'arabic', countryCode: 'SA', name: 'العربية' },       // ~400 million
    { code: 'portuguese', countryCode: 'BR', name: 'Português' }, // ~250 million
    { code: 'german', countryCode: 'DE', name: 'Deutsch' },       // ~130 million
    { code: 'japanese', countryCode: 'JP', name: '日本語' },       // ~125 million
    { code: 'korean', countryCode: 'KR', name: '한국어' }         // ~80 million
];

// Content languages (only English and French)
export const CONTENT_LANGUAGES = [
    { code: 'english', countryCode: 'US', name: 'English' },
    { code: 'french', countryCode: 'FR', name: 'Français' }
];

// Get ordered languages based on Quebec status
export const getOrderedLanguages = (isQuebec) => {
    if (isQuebec) {
        // In Quebec: French first, then others
        const french = LANGUAGES.find(l => l.code === 'french');
        const others = LANGUAGES.filter(l => l.code !== 'french');
        return [french, ...others].filter(Boolean);
    } else {
        // Other regions: English first, then others
        const english = LANGUAGES.find(l => l.code === 'english');
        const others = LANGUAGES.filter(l => l.code !== 'english');
        return [english, ...others].filter(Boolean);
    }
};

// Get ordered content languages based on Quebec status
export const getOrderedContentLanguages = (isQuebec) => {
    if (!SUPPORT_FRENCH) {
        const english = CONTENT_LANGUAGES.find((l) => l.code === "english");
        return english ? [english] : [];
    }
    if (isQuebec) {
        // In Quebec: French first, then English
        const french = CONTENT_LANGUAGES.find(l => l.code === 'french');
        const english = CONTENT_LANGUAGES.find(l => l.code === 'english');
        return [french, english].filter(Boolean);
    } else {
        // Other regions: English first, then French
        const english = CONTENT_LANGUAGES.find(l => l.code === 'english');
        const french = CONTENT_LANGUAGES.find(l => l.code === 'french');
        return [english, french].filter(Boolean);
    }
};

// Helper function to get content language from localStorage
export const getContentLanguage = () => {
    try {
        const stored = localStorage.getItem(CONTENT_LANG_KEY);
        const lang = stored || "english";
        if (!SUPPORT_FRENCH && lang === "french") {
            return "english";
        }
        return lang;
    } catch (error) {
        console.error('Error reading content language from localStorage:', error);
        return 'english';
    }
};

/** Fired in the same tab when content language is updated (cf. `storage` for other tabs). */
export const CONTENT_LANG_CHANGED_EVENT = "content-lang-changed";

// Helper function to set content language in localStorage
export const setContentLanguage = (language) => {
    try {
        const effective =
            !SUPPORT_FRENCH && language === "french" ? "english" : language;
        localStorage.setItem(CONTENT_LANG_KEY, effective);
        if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent(CONTENT_LANG_CHANGED_EVENT, { detail: { language: effective } }));
        }
    } catch (error) {
        console.error('Error saving content language to localStorage:', error);
    }
};

/** BCP 47 primary subtag → our interface language code (must match {@link LANGUAGES}). */
const BROWSER_PRIMARY_TO_INTERFACE = {
    en: 'english',
    fr: 'french',
    zh: 'chinese',
    hi: 'hindi',
    es: 'spanish',
    ar: 'arabic',
    pt: 'portuguese',
    de: 'german',
    ja: 'japanese',
    ko: 'korean'
};

/**
 * Maps the browser's preferred language(s) to one of our supported UI languages.
 * If none match, returns `english`.
 * Safe to call in SSR-free environments (uses `navigator` when available).
 */
export function detectBrowserInterfaceLanguage() {
    if (typeof navigator === 'undefined') {
        return 'english';
    }
    const candidates = [];
    try {
        if (navigator.languages && navigator.languages.length > 0) {
            candidates.push(...navigator.languages);
        } else if (navigator.language) {
            candidates.push(navigator.language);
        }
    } catch {
        return 'english';
    }
    for (const raw of candidates) {
        if (!raw || typeof raw !== 'string') continue;
        const norm = raw.toLowerCase().replace(/_/g, '-');
        const primary = norm.split('-')[0];
        if (primary === 'zh') {
            return 'chinese';
        }
        const mapped = BROWSER_PRIMARY_TO_INTERFACE[primary];
        if (mapped) {
            return mapped;
        }
    }
    return 'english';
}

// Helper function to get interface language from localStorage
export const getInterfaceLanguage = () => {
    try {
        const stored = localStorage.getItem(INTERFACE_LANG_KEY);
        return resolveInterfaceLang(stored);
    } catch (error) {
        console.error('Error reading interface language from localStorage:', error);
        return 'english';
    }
};

/** Custom event name for UI language changes (same-tab sync for i18n) */
export const INTERFACE_LANG_CHANGED_EVENT = "ui-lang-changed";

// Helper function to set interface language in localStorage
export const setInterfaceLanguage = (language) => {
    try {
        const resolved = normalizeLangParam(language);
        if (!resolved) return;
        localStorage.setItem(INTERFACE_LANG_KEY, resolved);
        if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent(INTERFACE_LANG_CHANGED_EVENT, { detail: { language: resolved } }));
        }
    } catch (error) {
        console.error('Error saving interface language to localStorage:', error);
    }
};

// Helper function to check if interface language is stored in localStorage
export const hasInterfaceLanguage = () => {
    try {
        return localStorage.getItem(INTERFACE_LANG_KEY) !== null;
    } catch (error) {
        console.error('Error checking interface language in localStorage:', error);
        return false;
    }
};

// Helper function to check if content language is stored in localStorage
export const hasContentLanguage = () => {
    try {
        return localStorage.getItem(CONTENT_LANG_KEY) !== null;
    } catch (error) {
        console.error('Error checking content language in localStorage:', error);
        return false;
    }
};

// Admin-specific content language functions (separate from user content language)
// Helper function to get admin content language from localStorage
export const getAdminContentLanguage = () => {
    try {
        const stored = localStorage.getItem(ADMIN_CONTENT_LANG_KEY);
        return stored || 'english'; // Default to English if not set
    } catch (error) {
        console.error('Error reading admin content language from localStorage:', error);
        return 'english';
    }
};

// Helper function to set admin content language in localStorage
export const setAdminContentLanguage = (language) => {
    try {
        localStorage.setItem(ADMIN_CONTENT_LANG_KEY, language);
    } catch (error) {
        console.error('Error saving admin content language to localStorage:', error);
    }
};

// Helper function to check if admin content language is stored in localStorage
export const hasAdminContentLanguage = () => {
    try {
        return localStorage.getItem(ADMIN_CONTENT_LANG_KEY) !== null;
    } catch (error) {
        console.error('Error checking admin content language in localStorage:', error);
        return false;
    }
};

